import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import { ChevronLeft, SaveAsOutlined } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { UseActiveLayer } from "shared/common/hooks/UseActiveLayer";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import {
  editTools,
  EditToolsType,
} from "shared/common/utils/editTools/editTools";

interface props {
  children: JSX.Element | JSX.Element[] | undefined;
}

export default function EditContent({ children }: props) {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const changeAlertSaveLayer = defaultStore(
    (state) => state.changeAlertSaveLayer
  );

  const editToolActivated = sidebarStore((state) => state.editToolActivated);
  const changeEditToolActivated = sidebarStore(
    (state) => state.changeEditToolActivated
  );
  const showEditToolsButtons = sidebarStore(
    (state) => state.showEditToolsButtons
  );
  const showToolDescription = sidebarStore(
    (state) => state.showToolDescription
  );
  const changeShowToolDescription = sidebarStore(
    (state) => state.changeShowToolDescription
  );
  const featureTableAddedRows = sidebarStore(
    (state) => state.featureTableAddedRows
  );
  const editMode = sidebarStore((state) => state.editMode);
  const changeEditMode = sidebarStore((state) => state.changeEditMode);

  const activeLayer = UseActiveLayer(viewer);

  const tools: EditToolsType[] = editTools();

  //open modal for saveing changes
  const saveChanges = () => {
    if (viewer === undefined) return;
    changeAlertSaveLayer(true);
  };

  //if active layer is line, polygon or linestring and can edited than make edit mode true else makes it false
  useEffect(() => {
    if (activeLayer) {
      if (
        (activeLayer.geometrytype == mm.GEOMETRYTYPE.LINESTRING ||
          activeLayer.geometrytype == mm.GEOMETRYTYPE.POLYGON ||
          activeLayer.geometrytype == mm.GEOMETRYTYPE.POINT) &&
        activeLayer.editable
      ) {
        changeEditMode(true);
      } else {
        changeEditMode(false);
      }
    }
  }, [activeLayer]);

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {showEditToolsButtons && editMode && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              backgroundColor: darkTheme ? "primary.light" : "grey.light",
            }}
          >
            {tools.map((item: EditToolsType) => (
              <Button
                id={item.id}
                key={item.id}
                disableRipple
                onClick={() =>
                  monitor(
                    apm,
                    "select-edit-tool | Edit",
                    () => changeEditToolActivated(item.id),
                    { editTool: item.title }
                  )
                }
                sx={{
                  width: "auto",
                  textAlign: "center",
                  color:
                    editToolActivated === item.id
                      ? "secondary.main"
                      : darkTheme
                        ? "white"
                        : "darkblue.main",
                  borderRadius: "0px",
                  backgroundColor:
                    editToolActivated === item.id
                      ? darkTheme
                        ? "darkblue.main"
                        : "white"
                      : "",
                }}
              >
                {item.icon}
                {editToolActivated === item.id && (
                  <Divider
                    sx={{
                      position: "absolute",
                      bottom: "0",
                      left: "50%",
                      backgroundColor: "secondary.main",
                      borderRadius: "4px 4px 0px 0px",
                      height: "2px",
                      marginLeft: "-12px",
                      width: "24px",
                    }}
                  />
                )}
              </Button>
            ))}
          </Box>
          <IconButton
            disabled={
              featureTableAddedRows === undefined ||
              featureTableAddedRows.length === 0
            }
            onClick={saveChanges}
            sx={{
              marginLeft: "auto",
              color: darkTheme ? "white" : "darkblue.main",
              backgroundColor: darkTheme ? "primary.light" : "grey.light",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
          >
            <SaveAsOutlined />
          </IconButton>
        </Box>
      )}
      {editMode && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            overflow: "auto",
            paddingRight: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              paddingBottom: showToolDescription ? 1 : 0,
              borderBottom: darkTheme ? "1px white solid" : "1px #053952 solid",
              transition: "padding 0.2s",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                overflow: "hidden",
              }}
            >
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: darkTheme ? "white" : "darkblue.main",
                }}
              >
                {
                  tools.find(
                    (item: EditToolsType) => item?.id === editToolActivated
                  )?.title
                }
              </Typography>
              {!showEditToolsButtons && (
                <IconButton
                  disabled={
                    featureTableAddedRows === undefined ||
                    featureTableAddedRows.length === 0
                  }
                  onClick={saveChanges}
                  sx={{
                    padding: 0,
                    marginLeft: "auto",
                    color: darkTheme ? "white" : "darkblue.main",
                    "@media (hover: hover)": {
                      ":hover": { color: "secondary.main" },
                    },
                  }}
                >
                  <SaveAsOutlined />
                </IconButton>
              )}
              <IconButton
                onClick={() =>
                  monitor(apm, "change-status-description | Edit", () =>
                    changeShowToolDescription(!showToolDescription)
                  )
                }
                size="small"
                sx={{
                  padding: 0,
                  marginLeft: showEditToolsButtons ? "auto" : "",
                  color: darkTheme ? "white" : "darkblue.main",
                  "@media (hover: hover)": {
                    ":hover": { color: "secondary.main" },
                  },
                  rotate: showToolDescription ? "90deg" : "-90deg",
                  transition: "rotate 0.2s",
                }}
              >
                <ChevronLeft />
              </IconButton>
            </Box>
            <Box
              sx={{
                overflow: "hidden",
              }}
            >
              <Typography
                dangerouslySetInnerHTML={{
                  __html:
                    tools.find(
                      (item: EditToolsType) => item?.id === editToolActivated
                    )?.description || "",
                }}
                sx={{
                  marginTop: showToolDescription ? "0%" : "-100%",
                  color: darkTheme ? "white" : "darkblue.main",
                  fontSize: 14,
                  fontWeight: 400,
                  transition: "margin 0.3s",
                }}
              ></Typography>
            </Box>
          </Box>
          <Box>{children}</Box>
        </Box>
      )}
      {!editMode && (
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 400,
            color: darkTheme ? "white" : "darkblue.main",
          }}
        >
          {t("edit_feature.not_editable_message")}
        </Typography>
      )}
    </Box>
  );
}
